<template>
  <Teleport to="body">
    <div class="modal-backdrop">
      <div class="modal-container vat-exemption-modal">
        <div class="modal-content">
          <div class="modal-header">
            <div class="modal-title-section">
              <h2 class="modal-title">{{ t('modals.vatExemptionCodesRequired') }}</h2>
              <p class="modal-subtitle">{{ t('modals.vatExemptionSubtitle') }}</p>
            </div>
            <button @click="$emit('close')" class="btn btn-icon">
              <font-awesome-icon icon="fa-solid fa-xmark" />
            </button>
          </div>

          <div class="modal-body">
            <div v-if="loading" class="loading-container">
              <div class="spinner-small"></div>
              <span>{{ t('modals.saving') }}</span>
            </div>

            <div v-else class="invoices-list">
              <div v-for="invoice in invoices" :key="invoice.invoice_id" class="invoice-item">
                <div class="invoice-thumbnail" @click="viewInvoice(invoice.invoice_id)">
                  <img
                    :src="`/api/invoices/${invoice.invoice_id}/original?thumbnail=true`"
                    :alt="invoice.invoice_number || 'Invoice'"
                    class="thumbnail-img"
                  />
                </div>
                
                <div class="invoice-content">
                  <div class="invoice-info">
                    <div class="invoice-header-line">
                      <font-awesome-icon icon="fa-solid fa-file-invoice" class="invoice-icon" />
                      <span class="invoice-number">{{ invoice.invoice_number }}</span>
                      <span v-if="invoice.vendor_name" class="separator">|</span>
                      <span v-if="invoice.vendor_name" class="vendor-name">{{ invoice.vendor_name }}</span>
                      <span class="vat-rate">VAT: {{ invoice.vat_rate }}%</span>
                    </div>
                  </div>
                  
                  <div class="invoice-fields">
                    <div class="field-group">
                      <label>{{ t('modals.vatCode') }}</label>
                      <select
                        v-model="invoice.vat_exemption_code"
                        class="form-control"
                        @change="updateInvoice(invoice)"
                      >
                        <option :value="null" disabled>{{ t('modals.selectCode') }}</option>
                        <option value="2">{{ t('modals.vatCodeOptions.2') }}</option>
                        <option value="3">{{ t('modals.vatCodeOptions.3') }}</option>
                        <option value="4">{{ t('modals.vatCodeOptions.4') }}</option>
                        <option value="5">{{ t('modals.vatCodeOptions.5') }}</option>
                        <option value="6">{{ t('modals.vatCodeOptions.6') }}</option>
                        <option value="7">{{ t('modals.vatCodeOptions.7') }}</option>
                        <option value="9">{{ t('modals.vatCodeOptions.9') }}</option>
                      </select>
                    </div>
                    
                    <div class="field-group">
                      <label>{{ t('modals.taxExemptionReason') }}</label>
                      <input
                        v-model="invoice.tax_exemption_reason"
                        type="text"
                        class="form-control"
                        :placeholder="t('modals.enterReason')"
                        @input="updateInvoice(invoice)"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="error" class="error-message">
              {{ error }}
            </div>
          </div>

          <div class="modal-footer">
            <button @click="$emit('close')" class="btn btn-secondary">
              {{ t('modals.cancel') }}
            </button>
            <button @click="saveVatCodes" class="btn btn-primary" :disabled="loading || !allCodesSelected">
              <font-awesome-icon v-if="loading" icon="fa-solid fa-spinner" spin />
              {{ t('modals.saveAndContinueExport') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import api from '@/utils/api';

const { t } = useI18n();

const props = defineProps({
  invoices: {
    type: Array,
    required: true
  }
});

const emit = defineEmits(['close', 'saved']);

const loading = ref(false);
const error = ref(null);

const allCodesSelected = computed(() => {
  return props.invoices.every(invoice => 
    invoice.vat_exemption_code && 
    invoice.vat_exemption_code !== null
  );
});

const updateInvoice = (invoice) => {
  // This function is called when user changes any field
  // We just need to ensure the reactive binding works
  error.value = null;
};

const viewInvoice = (invoiceId) => {
  // Open invoice in new tab
  window.open(`/api/invoices/${invoiceId}/original`, '_blank');
};

const saveVatCodes = async () => {
  // Check if all invoices have VAT codes
  const missingCodes = props.invoices.filter(inv => !inv.vat_exemption_code || inv.vat_exemption_code === null);
  if (missingCodes.length > 0) {
    error.value = `Please select VAT codes for all ${missingCodes.length} invoice(s)`;
    return;
  }

  loading.value = true;
  error.value = null;

  try {
    // Save VAT exemption codes for all invoices
    const promises = props.invoices.map(invoice => 
      api.put(`/api/scans/${invoice.invoice_id}/update-vat-exemption`, {
        vat_exemption_code: invoice.vat_exemption_code,
        tax_exemption_reason: invoice.tax_exemption_reason || ''
      })
    );

    await Promise.all(promises);
    
    emit('saved');
  } catch (err) {
    console.error('Failed to save VAT exemption codes:', err);
    
    if (err.response?.data?.message) {
      error.value = err.response.data.message;
    } else {
      error.value = 'Failed to save VAT exemption codes. Please try again.';
    }
  } finally {
    loading.value = false;
  }
};
</script>

<style scoped>
.modal-backdrop {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 9998;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  overflow-x: auto;
}

.modal-container {
  position: relative;
  z-index: 9999;
  width: 1100px;
  min-width: 1100px;
  margin: 0 auto;
}

.vat-exemption-modal {
  max-width: 1100px;
  width: 1100px;
}

.modal-content {
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 24px 28px;
  border-bottom: 1px solid #e5e7eb;
  background: linear-gradient(to bottom, #ffffff, #fafbfc);
  gap: 16px;
  border-radius: 12px 12px 0 0;
}

.modal-title-section {
  flex: 1;
}

.modal-title {
  font-size: 22px;
  font-weight: 700;
  margin: 0;
  color: #111827;
  letter-spacing: -0.01em;
}

.modal-subtitle {
  font-size: 14px;
  color: #6b7280;
  margin-top: 6px;
  margin-bottom: 0;
  font-weight: 400;
}

.btn-icon {
  background: transparent;
  border: none;
  padding: 8px;
  cursor: pointer;
  color: #6b7280;
  border-radius: 6px;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-icon:hover {
  background: #f3f4f6;
  color: #374151;
}

.modal-body {
  padding: 24px 28px;
  overflow-y: auto;
  flex: 1;
}

.loading-container {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 40px;
  color: #6b7280;
  font-size: 15px;
}

.spinner-small {
  width: 20px;
  height: 20px;
  border: 2px solid #e5e7eb;
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.invoices-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.invoice-item {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding: 16px 18px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  gap: 16px;
  background: white;
  transition: all 0.2s;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.invoice-item:hover {
  border-color: #cbd5e1;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  transform: translateY(-1px);
}

.invoice-thumbnail {
  width: 60px;
  height: 60px;
  border-radius: 8px;
  overflow: hidden;
  background: #f1f5f9;
  border: 2px solid #e2e8f0;
  flex-shrink: 0;
  cursor: pointer;
  transition: all 0.2s ease;
}

.invoice-thumbnail:hover {
  border-color: #0ea5e9;
  transform: scale(1.05);
}

.thumbnail-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.invoice-content {
  display: flex;
  flex-direction: column;
  gap: 14px;
  flex: 1;
  min-width: 0;
}

.invoice-info {
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 0 0 auto;
}

.invoice-header-line {
  display: flex;
  align-items: center;
  gap: 12px;
}

.invoice-icon {
  color: #3b82f6;
  font-size: 16px;
  flex-shrink: 0;
}

.invoice-number {
  font-weight: 700;
  color: #0f172a;
  font-size: 15px;
}

.separator {
  color: #cbd5e1;
  font-weight: 400;
  font-size: 15px;
}

.vendor-name {
  color: #64748b;
  font-weight: 500;
}

.vat-rate {
  color: #ea580c;
  font-weight: 600;
  padding: 2px 8px;
  background: #ffedd5;
  border-radius: 4px;
  font-size: 12px;
  margin-left: 12px;
}

.invoice-fields {
  display: grid;
  grid-template-columns: 240px 480px;
  gap: 12px;
}

.field-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.field-group label {
  font-size: 12px;
  font-weight: 600;
  color: #374151;
  text-transform: uppercase;
  letter-spacing: 0.025em;
}

.field-group .form-control {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 400;
  background: white;
  color: #1f2937;
  transition: all 0.2s ease;
  min-height: 38px;
}

.field-group select.form-control {
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 448 512'%3E%3Cpath fill='%236b7280' d='M201.4 406.6c12.5 12.5 32.8 12.5 45.3 0l192-192c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L224 338.7 54.6 169.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3l192 192z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  background-size: 11px;
  padding-right: 36px;
}

.field-group .form-control:hover {
  border-color: #d1d5db;
}

.field-group .form-control:focus {
  outline: none;
  border-color: #0ea5e9;
  box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.1);
}

.error-message {
  padding: 14px 18px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  color: #991b1b;
  margin-top: 16px;
  font-size: 14px;
  font-weight: 500;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 20px 28px;
  border-top: 1px solid #e5e7eb;
  background: #fafbfc;
  border-radius: 0 0 12px 12px;
}

.btn {
  padding: 11px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  display: flex;
  align-items: center;
  gap: 8px;
  letter-spacing: 0.01em;
}

.btn-secondary {
  background: white;
  color: #374151;
  border: 2px solid #e5e7eb;
}

.btn-secondary:hover {
  background: #f9fafb;
  border-color: #d1d5db;
}

.btn-primary {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

.btn-primary:hover:not(:disabled) {
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
  box-shadow: 0 6px 16px rgba(59, 130, 246, 0.4);
  transform: translateY(-1px);
}

.btn-primary:disabled {
  background: #d1d5db;
  color: #9ca3af;
  cursor: not-allowed;
  box-shadow: none;
}

/* Mobile Responsive Styles */
@media (max-width: 768px) {
  .modal-backdrop {
    padding: 0;
    overflow-y: auto;
  }

  .modal-container {
    width: 100%;
    min-width: auto;
    max-width: 100%;
    margin: 0;
  }

  .vat-exemption-modal {
    width: 100%;
    max-width: 100%;
    min-width: auto;
  }

  .modal-content {
    border-radius: 0;
    max-height: 100vh;
    min-height: 100vh;
  }

  .modal-header {
    padding: 12px 16px;
    gap: 8px;
  }

  .modal-title-section {
    padding-right: 24px;
  }

  .modal-title {
    font-size: 16px;
    line-height: 1.3;
  }

  .modal-subtitle {
    font-size: 12px;
    line-height: 1.4;
  }

  .close-btn {
    position: absolute;
    top: 8px;
    right: 8px;
    width: 32px;
    height: 32px;
    padding: 6px;
  }

  .modal-body {
    padding: 12px;
    gap: 12px;
    overflow-y: auto;
  }

  .invoice-item {
    padding: 12px;
    gap: 10px;
    flex-direction: column;
  }

  .invoice-thumbnail {
    display: none;
  }

  .invoice-content {
    margin-left: 0;
    width: 100%;
  }

  .invoice-header-line {
    flex-wrap: wrap;
    gap: 4px 6px;
    font-size: 12px;
  }

  .invoice-icon {
    font-size: 14px;
  }

  .invoice-number {
    font-size: 13px;
  }

  .vendor-name {
    font-size: 12px;
    word-break: break-word;
  }

  .separator {
    font-size: 13px;
  }

  .vat-rate {
    padding: 2px 6px;
    font-size: 10px;
  }

  .invoice-fields {
    display: flex;
    flex-direction: column;
    gap: 12px;
    grid-template-columns: none;
  }

  .field-group {
    width: 100%;
  }

  .field-group label {
    font-size: 12px;
    font-weight: 600;
    margin-bottom: 6px;
  }

  .form-control {
    font-size: 14px;
    padding: 10px 12px;
    width: 100%;
  }

  .modal-footer {
    padding: 12px 16px;
    flex-direction: column-reverse;
    gap: 10px;
    border-top: 1px solid #e5e7eb;
  }

  .btn {
    width: 100%;
    justify-content: center;
    font-size: 15px;
    padding: 12px 16px;
  }
}

</style>
